import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/Layout/MainLayout';
import { Mail, Globe, MapPin, Clock, Info as InfoIcon, Shield, Leaf, Linkedin, Youtube, Send, Loader2 } from 'lucide-react';
import SupportTicketWidget from '../components/SupportTicketWidget';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const SUPPORT_EMAIL = 'support@emahq.io';

const ContactSupport: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const userName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() : '';

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast({ variant: 'destructive', title: t('support.messageRequired'), description: t('support.messageRequiredDesc') });
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-support-request', {
        body: {
          name: userName || undefined,
          email: user?.email,
          subject: subject.trim() || undefined,
          category,
          message: message.trim(),
          source: 'in-app',
          user_id: user?.id,
        },
      });
      if (error || !(data as { success?: boolean })?.success) {
        throw new Error(error?.message || 'Request failed');
      }
      toast({ title: t('support.requestSent'), description: t('support.requestSentDesc') });
      setSubject('');
      setMessage('');
      setCategory('general');
    } catch (err) {
      // Fall back to a mailto draft so nothing is lost.
      const body = encodeURIComponent(`${message}\n\n— ${userName || user?.email || ''}`);
      const subj = encodeURIComponent(subject || t('support.mailtoSubject'));
      window.open(`mailto:${SUPPORT_EMAIL}?subject=${subj}&body=${body}`);
      toast({
        variant: 'destructive',
        title: t('support.couldNotSend'),
        description: t('support.couldNotSendDesc'),
      });
    } finally {
      setSending(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: t('support.emailSupport'),
      value: SUPPORT_EMAIL,
      description: t('support.generalInquiries'),
      action: () => window.open(`mailto:${SUPPORT_EMAIL}`)
    },
    {
      icon: Globe,
      title: t('support.website'),
      value: 'www.growth-energy.fr',
      description: t('support.visitWebsite'),
      action: () => window.open('https://www.growth-energy.fr', '_blank')
    }
  ];

  const socialContacts = [
    {
      icon: Linkedin,
      title: 'LinkedIn',
      value: 'Growth Energy',
      action: () => window.open('https://linkedin.com/company/growthenergy0', '_blank'),
      color: 'bg-blue-700 hover:bg-blue-800'
    },
    {
      icon: Youtube,
      title: 'YouTube',
      value: '@GrowthEnergy1',
      action: () => window.open('https://youtube.com/@GrowthEnergy1', '_blank'),
      color: 'bg-red-600 hover:bg-red-700'
    }
  ];

  const officeHours = [
    { day: t('support.mondayFriday'), hours: '8:00 AM - 6:00 PM' },
    { day: t('support.saturday'), hours: '9:00 AM - 2:00 PM' },
    { day: t('support.sunday'), hours: t('support.emergencyOnly') }
  ];

  return (
    <MainLayout title={t('support.pageTitle')}>
      <div className="p-4 space-y-6 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <h2 className="text-xl font-semibold mb-2">{t('support.getInTouch')}</h2>
          <p className="text-blue-100">{t('support.getInTouchDesc')}</p>
        </div>

        {/* Raise a ticket */}
        <SupportTicketWidget />

        {/* Email support team */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Mail className="w-6 h-6 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('support.emailEmaSupport')}</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('support.emailSupportDesc', { email: user?.email || t('support.yourAccountEmail') })}
          </p>
          <form onSubmit={handleSupportSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="support-subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('support.subject')}</label>
                <input
                  id="support-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('support.subjectPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="support-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('support.category')}</label>
                <select
                  id="support-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">{t('support.catGeneral')}</option>
                  <option value="technical">{t('support.catTechnical')}</option>
                  <option value="billing">{t('support.catBilling')}</option>
                  <option value="metering">{t('support.catMetering')}</option>
                  <option value="account">{t('support.catAccount')}</option>
                  <option value="other">{t('support.catOther')}</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="support-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('support.message')}</label>
              <textarea
                id="support-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                required
                placeholder={t('support.messagePlaceholder')}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium px-5 py-2.5 text-sm transition-colors"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? t('support.sending') : t('support.sendToSupport')}
            </button>
          </form>
        </div>

        {/* Contact Methods */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('support.contactInformation')}</h3>
          <div className="space-y-4">
            {contactInfo.map((contact, index) => {
              const Icon = contact.icon;
              return (
                <button
                  key={index}
                  onClick={contact.action}
                  className="w-full flex items-center space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{contact.title}</p>
                    <p className="text-blue-600 dark:text-blue-400 font-medium">{contact.value}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{contact.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('support.connectWithUs')}</h3>
          <div className="grid grid-cols-1 gap-4">
            {socialContacts.map((social, index) => {
              const Icon = social.icon;
              return (
                <button
                  key={index}
                  onClick={social.action}
                  className={`w-full flex items-center space-x-4 p-4 text-white rounded-lg transition-colors ${social.color}`}
                >
                  <Icon className="w-6 h-6" />
                  <div className="text-left">
                    <p className="font-medium">{social.title}</p>
                    <p className="text-sm opacity-90">{social.value}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Office Hours */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="w-6 h-6 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('support.officeHours')}</h3>
          </div>
          <div className="space-y-2">
            {officeHours.map((schedule, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                <span className="font-medium text-gray-900 dark:text-white">{schedule.day}</span>
                <span className="text-gray-600 dark:text-gray-400">{schedule.hours}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Offices */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('support.ourOffices')}</h3>
          </div>
          <div className="space-y-4">
            {/* European HQ */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">{t('support.europeanHq')}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Administrative: 212 Chemin de la Madrague-Ville, 13015 Marseille, France<br />
                Social Office: 48 Boulevard Saade – Quai Lazaret, 13002 Marseille, France
              </p>
            </div>
            {/* Paris */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">{t('support.parisOffice')}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                4 Rue De Longchamp, 75116 Paris, France
              </p>
            </div>
            {/* African HQ */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">{t('support.africanHq')}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                GEFI Solutions SEZ Limited, 4th Floor, North Tower<br />
                Two Rivers Finance and Innovation Center, Nairobi, Kenya
              </p>
            </div>
            {/* Tanzania */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">{t('support.zanzibarOffice')}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Building D11-04, Fumba Town, Zanzibar
              </p>
            </div>
            {/* Nigeria */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">{t('support.nigeriaOffices')}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Main: 9, A-Avenue, Citec Estate, Mbora District, Abuja<br />
                Showroom: Shops A1-25 and A1-26, Dutse Ultramodern Market, Abuja
              </p>
            </div>
            {/* Burundi */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">{t('support.burundiOffices')}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Main: 31 Rue Mugamba, Bujumbura, Burundi<br />
                E-Mobility Station: GEM E-Mobility, Mukaza Avenue Nzero, Bujumbura
              </p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-2">
            <InfoIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="font-semibold text-gray-800 dark:text-gray-200">{t('support.aboutGrowthEnergy')}</h3>
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
            {t('support.aboutGrowthEnergyDesc')}
          </p>
        </div>

        {/* Services */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('support.ourServices')}</h3>
          </div>
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">{t('support.smartMetering')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('support.smartMeteringDesc')}</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">{t('support.deviceControl')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('support.deviceControlDesc')}</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">{t('support.energyOptimization')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('support.energyOptimizationDesc')}</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">{t('support.billingPayments')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('support.billingPaymentsDesc')}</p>
            </div>
          </div>
        </div>

        {/* Sustainability */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Leaf className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('support.sustainabilityCommitment')}</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('support.sustainabilityDesc')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-green-600">25%</span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{t('support.averageEnergySavings')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-blue-600">10K+</span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{t('support.co2Avoided')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-purple-600">100%</span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{t('support.renewableGoal')}</p>
            </div>
          </div>
        </div>

        {/* App Version */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
          <div className="text-center text-gray-600 dark:text-gray-400">
            <p className="text-sm">{t('support.appName')}</p>
            <p className="text-xs">{t('support.appVersion')}</p>
          </div>
        </div>

        {/* Growth Energy Logo */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm text-center">
          <img src={import.meta.env.VITE_APP_LOGO} alt="EMA" className="w-48 h-auto mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('support.poweredByMfg')}</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t('support.membersOfSolio')}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('support.leadingFuture')}
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ContactSupport;
